import Inventory from '../entities/inventory'
import Memory from '../entities/memory'
import Tiles from '../entities/tiles'
import Player from '../entities/player'
import Stack from '../entities/stack'
import TileSprite from '../entities/tileSprite'
import { Values } from '../entities/values'
import Laser from '../entities/laser'

const COORDINATES = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F']

export default class LevelState extends Phaser.State {
  init (levelIndex) {
    this.levelIndex = levelIndex
  }

  preload () {
    this.game.load.tilemap(`level-${this.levelIndex}`, ASSETS.getLevelData(this.levelIndex), null, Phaser.Tilemap.TILED_JSON)
  }

  create (game) {
    this.keyQueue = this.setupKeyQueue()

    this.levelGroup = this.game.add.group(undefined, 'level')
    this.levelGroup.x = 16
    this.levelGroup.y = 16

    this.memory = new Memory(16, 12)

    if (this.levelIndex !== 0) {
      this.coordinateText = this.setupBorderText()
    }

    this.setupTileMap()

    this.values = new Values(this.game, this.memory.offset(0, 12), 12, 4)
    this.values.x = TILE_WIDTH * 12
    this.values.y = 0
    this.levelGroup.add(this.values)

    if (this.levelIndex === 0) {
      this.values.alpha = 0
    }

    this.tiles = this.setupTiles()
    this.objectGroup = this.game.add.group(this.levelGroup, 'object')

    this.entities = []

    this.player = this.createSingleObjectSprite({ name: 'player' }, {
      key: 'sloth',
      constructor: Player,
      group: this.objectGroup
    })

    const lasers = this.createObjectSprites({ name: 'laser' }, {
      key: 'sloth',
      constructor: Laser,
      group: this.objectGroup
    })

    this.player.bringToTop()

    this.entities = this.entities.concat(lasers)

    this.setPlayerCoordinateTint(true)

    this.stack = new Stack(this.game, this.memory.offset(0, 12), 12, 4)
    this.stack.x = 192
    this.levelGroup.add(this.stack)

    this.populateStack()

    this.inventory = this.setupInventory()
    this.setupItemKeys()

    this.inventory.onItemChanged.add(this.stack.onItemChanged.bind(this.stack))

    this.inventory.selectFirstItem()

    const resetKey = this.game.input.keyboard.addKey(Phaser.Keyboard.R)
    resetKey.onDown.add((key) => {
      if (key.event.metaKey) {
        window.location.reload()
      } else {
        this.reset()
      }
    })

    this.paused = false
    this.fadeIn()

    const escapeKey = this.game.input.keyboard.addKey(Phaser.Keyboard.ESC)
    escapeKey.onDown.add(() => { this.state.start('init', true, false, 0) })
  }

  update () {
    if (this.paused) {
      return
    }

    if (this.player.tileX < 0 || this.player.tileX > 11 || this.player.tileY < 0 || this.player.tileY > 11) {
      this.displayFailMessage('OUT OF BOUNDS ERROR')
    }

    if (this.player.moving) {
      this.checkPlayerTile()
    }

    this.movePlayer()

    for (let e of this.entities) {
      e.updateBeams(this.tileMap, this.entities)
    }
  }

  setupItemKeys () {
    const number_keys = ['ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'ZERO']
    for (let i in number_keys) {
      const key = this.game.input.keyboard.addKey(Phaser.Keyboard[number_keys[i]])
      key.onDown.add(() => { this.inventory.selectItem(i) })
    }

    ;[Phaser.Keyboard.ENTER, Phaser.Keyboard.M].forEach((key) => {
      const use_key = this.game.input.keyboard.addKey(key)
      use_key.onDown.add(() => {
        try {
          if (!this.paused) this.inventory.useSelectedItem()
        } catch (e) {
          if (e.name !== 'OutOfBoundsError') {
            throw e
          }

          this.displayFailMessage('OUT OF BOUNDS ERROR')
        }
      })
    })

    const previousItemKey = this.game.input.keyboard.addKey(Phaser.Keyboard.Q)
    previousItemKey.onDown.add(() => { if (!this.paused) this.inventory.previousItem() })
    const nextItemKey = this.game.input.keyboard.addKey(Phaser.Keyboard.E)
    nextItemKey.onDown.add(() => { if (!this.paused) this.inventory.nextItem() })
  }

  setupTiles () {
    const tiles = new Tiles(this.game, this.levelGroup, this.memory.offset(0, 0), this.tileMap, this.map, this.levelIndex)

    const shiftKey = this.game.input.keyboard.addKey(Phaser.Keyboard.SHIFT)
    shiftKey.onDown.add(() => { if (!this.paused) tiles.setHighlight(true) })
    shiftKey.onUp.add(() => { if (!this.paused) tiles.setHighlight(false) })

    return tiles
  }

  setupTileMap () {
    this.tileMap = this.game.add.tilemap(`level-${this.levelIndex}`)
    if (!this.tileMap.properties) this.tileMap.properties = {}
    const tilesetIndex = this.tileMap.getTilesetIndex('tiles')
    const tileProperties = this.tileMap.tilesets[tilesetIndex].tileProperties
    for (var tileIndex = 0; tileIndex < 256; tileIndex++) {
      if (!tileProperties[tileIndex]) {
        tileProperties[tileIndex] = tileProperties[81]
      }

      // Set glyph values based on the map
      const glyph = tileProperties[tileIndex]['type']
      if (glyph && glyph.startsWith('glyph_')) {
        const glyph_value = this.tileMap.properties[glyph]
        if (glyph_value) {
          tileProperties[tileIndex]['value'] = glyph_value
          const tilesInMap = this.getTilesByType(glyph, 'tiles')
          tilesInMap.forEach((t) => { t.properties.value = glyph_value })
        }
      }
    }

    window.tileMap = this.tileMap
    this.tileMap.addTilesetImage('tiles', 'tilemap')

    this.map = this.tileMap.createLayer('tiles')
    this.map.resizeWorld()
    this.levelGroup.add(this.map)
  }

  getTilesByType (type, tileLayer) {
    const tiles = []
    for (let i = 0; i < 12; i++) {
      for (let j = 0; j < 12; j++) {
        const tile = this.tileMap.getTile(i, j, tileLayer)
        if (tile.properties.type === type) tiles.push(tile)
      }
    }

    return tiles
  }

  setupInventory () {
    const inventory = new Inventory(this.game, this.stack, this.player)
    this.levelGroup.add(inventory)
    inventory.y = 12 * TILE_HEIGHT

    for (let item in this.tileMap.properties) {
      if (item.startsWith('item_')) {
        const count = this.tileMap.properties[item]
        inventory.addItem(item, count)
      }
    }

    return inventory
  }

  populateStack () {
    const stackValues = this.tileMap.properties.stack
    if (stackValues) {
      stackValues.split(',').reverse().forEach((stackValue) => {
        this.stack.push(parseInt(stackValue))
      })
    }
  }

  checkPlayerTile () {
    const tile = this.tileMap.getTile(this.player.tileX, this.player.tileY)
    switch (tile.properties.type) {
      case 'destination':
        this.goToLevel(this.levelIndex + 1)
        break
      case 'error':
        this.displayFailMessage('YOU DIED')
        break
    }

    if (!this.canMove(this.player, tile, false)) {
      this.displayFailMessage('YOU DIED')
    }
  }

  setupBorderText () {
    const coordinateText = {
      x: [],
      y: []
    }

    for (let i = 0; i < 16; i++) {
      const text = this.game.add.bitmapText(16 * i + 6, -12, 'monospace', COORDINATES[i], 9, this.levelGroup)
      coordinateText.x[i] = text
    }

    for (let i = 0; i < 12; i++) {
      const text = this.game.add.bitmapText(-10, 16 * i + 4, 'monospace', COORDINATES[i], 9, this.levelGroup)
      coordinateText.y[i] = text
    }

    return coordinateText
  }

  setupKeyQueue () {
    const keyQueue = []
    const cursors = this.game.input.keyboard.createCursorKeys()
    const wasd = {
      up: this.game.input.keyboard.addKey(Phaser.Keyboard.W),
      down: this.game.input.keyboard.addKey(Phaser.Keyboard.S),
      left: this.game.input.keyboard.addKey(Phaser.Keyboard.A),
      right: this.game.input.keyboard.addKey(Phaser.Keyboard.D)
    }

    ;[cursors, wasd].forEach((keySet) => {
      for (let k in keySet) {
        const cursor = keySet[k]

        cursor.onDown.add(() => {
          if (!this.paused && this.keyQueue.indexOf(cursor.keyCode) === -1) {
            this.keyQueue.push(cursor.keyCode)
          }
        })

        cursor.onUp.add(() => {
          const index = this.keyQueue.indexOf(cursor.keyCode)
          if (!this.paused && index > -1) {
            this.keyQueue.splice(index, 1)
          }
        })
      }
    })

    return keyQueue
  }

  createSingleObjectSprite (spec, options) {
    const objects = this.createObjectSprites(spec, options)
    if (objects.length !== 1) {
      throw new Error(`Must have one ${JSON.stringify(spec)}`)
    }
    return objects[0]
  }

  createObjectSprites (spec, options) {
    const objects = []
    this.tileMap.objects['objects'].forEach((object) => {
      if (!matchesSpec(object, spec)) {
        return
      }

      const constructor = options.constructor || TileSprite
      const key = options.key || 'tilemap'
      const group = options.group || this.game
      const sprite = new constructor(this.game, object.x, object.y - TILE_HEIGHT, key, object.gid - 1)

      sprite.name = object.name

      for (let property in object.properties) {
        sprite[property] = object.properties[property]
      }

      objects.push(sprite)

      if (typeof group.add === 'function') group.add(sprite)
      else group.add.existing(sprite)

      if (sprite.registerValues) {
        sprite.registerValues(this.values.next())
      }
    })
    return objects
  }

  movePlayer () {
    if (this.keyQueue.length === 0) {
      return
    }

    let currentKey = this.keyQueue[this.keyQueue.length - 1]
    const targetTile = this.getTargetTile(this.player, currentKey)
    window.targetTile = targetTile

    switch (currentKey) {
      case Phaser.Keyboard.W: currentKey = Phaser.Keyboard.UP; break
      case Phaser.Keyboard.D: currentKey = Phaser.Keyboard.RIGHT; break
      case Phaser.Keyboard.S: currentKey = Phaser.Keyboard.DOWN; break
      case Phaser.Keyboard.A: currentKey = Phaser.Keyboard.LEFT; break
    }

    if (!this.canMove(this.player, targetTile)) {
      this.player.setDirection(currentKey)
      return
    }

    this.setPlayerCoordinateTint(false)

    switch (currentKey) {
      case Phaser.Keyboard.UP:
        this.player.move(currentKey, 0, -1)
        break
      case Phaser.Keyboard.RIGHT:
        this.player.move(currentKey, 1, 0)
        break
      case Phaser.Keyboard.DOWN:
        this.player.move(currentKey, 0, 1)
        break
      case Phaser.Keyboard.LEFT:
        this.player.move(currentKey, -1, 0)
        break
    }

    this.setPlayerCoordinateTint(true)
  }

  getTargetTile (entity, currentKey) {
    switch (currentKey) {
      case Phaser.Keyboard.W: currentKey = Phaser.Keyboard.UP; break
      case Phaser.Keyboard.D: currentKey = Phaser.Keyboard.RIGHT; break
      case Phaser.Keyboard.S: currentKey = Phaser.Keyboard.DOWN; break
      case Phaser.Keyboard.A: currentKey = Phaser.Keyboard.LEFT; break
    }

    let getAdjacentTile
    switch (currentKey) {
      case Phaser.Keyboard.UP:
        getAdjacentTile = 'getTileAbove'
        break
      case Phaser.Keyboard.RIGHT:
        getAdjacentTile = 'getTileRight'
        break
      case Phaser.Keyboard.DOWN:
        getAdjacentTile = 'getTileBelow'
        break
      case Phaser.Keyboard.LEFT:
        getAdjacentTile = 'getTileLeft'
        break
    }

    const mapLayerIndex = this.tileMap.getLayerIndex('tiles')
    return this.tileMap[getAdjacentTile](mapLayerIndex, entity.tileX, entity.tileY)
  }

  canMove (entity, targetTile, checkMoving = true) {
    if (checkMoving && entity.moving) {
      return false
    }

    for (let ent of this.entities) {
      if (ent.wouldCollide(targetTile.x, targetTile.y)) {
        return false
      }
    }

    return !targetTile.properties.collidable
  }

  setPlayerCoordinateTint (tint) {
    if (this.levelIndex !== 0) {
      this.coordinateText.x[this.player.tileX].tint = (tint ? 0xFF0000 : 0xFFFFFF)
      this.coordinateText.y[this.player.tileY].tint = (tint ? 0xFF0000 : 0xFFFFFF)
    }
  }

  reset () {
    this.goToLevel(this.levelIndex, true)
  }

  goToLevel (levelIndex, skipIntro = false) {
    if (levelIndex == 22) {
      this.fadeOut().onComplete.add(() => (this.state.start('credits', true, false, 0)))
    } else {
      if (skipIntro) {
        this.fadeOut().onComplete.add(() => (this.state.start('level', true, false, levelIndex)))
      } else {
        this.fadeOut().onComplete.add(() => (this.state.start('text', true, false, levelIndex)))
      }
    }
  }

  displayFailMessage (title) {
    this.paused = true

    const centerX = this.game.canvas.width / 2
    const centerY = this.game.canvas.height / 2

    const titleWidth = title.length * 6

    const messageGroup = this.game.add.group(undefined, 'message')

    const messageBox = this.game.add.graphics(centerX, centerY, messageGroup)
    messageBox.beginFill('0x000000')
    messageBox.lineStyle(2, 0x660000, 1)
    messageBox.drawRect(-60, -25, 120, 50)
    messageBox.endFill()

    const messageTitleText = this.game.add.bitmapText(centerX - titleWidth / 2 + 1, centerY - 18, 'monospace', title, 9, messageGroup)
    messageTitleText.tint = 0xff0000

    this.game.add.bitmapText(centerX - (7 * 6) / 2 + 1, centerY - 3, 'monospace', 'PRESS R', 9, messageGroup)
    this.game.add.bitmapText(centerX - (10 * 6) / 2 + 1, centerY + 9, 'monospace', 'TO RESTART', 9, messageGroup)

    messageGroup.alpha = 0
    this.game.add.tween(messageGroup)
      .to({
        alpha: 1
      }, 200, Phaser.Easing.Linear.None, true)
  }

  fadeIn () {
    const graphic = this.game.add.graphics()
    graphic.beginFill(0x000000, 1)
    graphic.drawRect(0, 0, this.game.width, this.game.height)
    graphic.endFill()
    graphic.alpha = 1
    this.game.add.tween(graphic).to({alpha: 0}, 500, Phaser.Easing.Linear.None, true)
  }

  fadeOut () {
    const graphic = this.game.add.graphics()
    graphic.beginFill(0x000000, 1)
    graphic.drawRect(0, 0, this.game.width, this.game.height)
    graphic.endFill()
    graphic.alpha = 0
    const tween = this.game.add.tween(graphic).to({alpha: 1}, 500, Phaser.Easing.Linear.None, true)

    return tween
  }
}

function matchesSpec(object, spec) {
  for (let k in spec) {
    if (object[k] !== spec[k]) {
      return false
    }
  }
  return true
}
